import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, forkJoin, map, of, tap } from 'rxjs';

import { API_V1_URL } from '../config/api.config';
import { LoginResponse } from '../../features/login/models/login-response.model';

export interface FilialeLookupDto {
  id: number;
  filialNummer: number;
  name?: string | null;
  aktiv?: boolean;
}

interface RolleInfo {
  id: string;
  name: string;
  istAdminRolle: boolean;
  module: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly isAdminRoleKey = 'isAdminRole';
  private readonly accessTokenKey = 'accessToken';
  private readonly refreshTokenKey = 'refreshToken';
  private readonly accessTokenExpiryKey = 'accessTokenExpiry';
  private readonly refreshTokenExpiryKey = 'refreshTokenExpiry';
  private readonly sessionReadyKey = 'authSessionReady';
  private readonly usernameKey = 'username';
  private readonly fullNameKey = 'fullName';
  private readonly filialenDetailsKey = 'allowedFilialenDetails';

  private readonly rolesKey = 'roles';
  private readonly modulesKey = 'allowedModules';
  private readonly filialenKey = 'allowedFilialen';

  readonly isLoggedIn = signal(this.hasValidSession());

  private readonly permissionVersion = signal(0);

  startPosSession(accessToken: string): boolean {
    const claims = this.decodeAccessToken(accessToken);
    const tokenSource = claims['ofs:token_source'];
    const expiry = this.getTokenExpiryIso(claims);

    if (tokenSource !== 'login_api' || !expiry) {
      return false;
    }

    this.clearSession();

    localStorage.setItem(this.accessTokenKey, accessToken);
    localStorage.setItem(this.accessTokenExpiryKey, expiry);
    localStorage.setItem(this.sessionReadyKey, 'false');

    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.refreshTokenExpiryKey);

    this.storeUserDataFromToken(accessToken);
    this.isLoggedIn.set(false);

    return true;
  }

  consumePosAccessTokenFromCurrentUrl(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const fragment = window.location.hash.replace(/^#/, '');

    if (!fragment) {
      return null;
    }

    const token = new URLSearchParams(fragment).get('token');

    if (!token) {
      return null;
    }

    const cleanUrl = `${window.location.pathname}${window.location.search}`;

    window.history.replaceState(
      window.history.state,
      document.title,
      cleanUrl
    );

    return token;
  }

  login(response: LoginResponse): void {
    this.clearRuntimePermissions();

    localStorage.setItem(
      this.sessionReadyKey,
      'false'
    );

    this.storeTokens(response);
    this.storeUserDataFromToken(response.accessToken);

    if (this.mustChangePassword()) {
      localStorage.setItem(
        this.sessionReadyKey,
        'true'
      );
    }

    this.isLoggedIn.set(
      this.hasValidSession()
    );
  }

  logout(): void {
    this.clearSession();
  }

  hydrateRuntimePermissions(): Observable<void> {
    if (!this.getToken()) {
      this.clearRuntimePermissions();

      return of(void 0);
    }

    if (
      this.mustChangePassword() ||
      this.isCopagoAdmin()
    ) {
      this.clearRuntimePermissions();
      this.completeSessionInitialization();

      return of(void 0);
    }

    const rolesRequest = this.isPosSession()
      ? of<RolleInfo[]>([])
      : this.http.get<RolleInfo[]>(
        `${API_V1_URL}/me/roles`
      );

    return forkJoin({
      modules: this.http.get<string[]>(
        `${API_V1_URL}/me/modules`
      ),

      filialen: this.http.get<FilialeLookupDto[]>(
        `${API_V1_URL}/me/filialen`
      ),

      roles: rolesRequest,
    }).pipe(
      tap(({ modules, filialen, roles }) => {
        localStorage.setItem(
          this.modulesKey,
          JSON.stringify(modules ?? [])
        );

        localStorage.setItem(
          this.filialenKey,
          JSON.stringify(
            (filialen ?? []).map(
              (filiale) =>
                String(filiale.id)
            )
          )
        );

        localStorage.setItem(
          this.filialenDetailsKey,
          JSON.stringify(
            filialen ?? []
          )
        );

        localStorage.setItem(
          this.rolesKey,
          JSON.stringify(
            (roles ?? []).map(
              (role) => role.name
            )
          )
        );

        localStorage.setItem(
          this.isAdminRoleKey,
          JSON.stringify(
            (roles ?? []).some(
              (role) =>
                role.istAdminRolle
            )
          )
        );

        this.permissionVersion.update(
          (value) => value + 1
        );

        this.completeSessionInitialization();
      }),

      map(() => void 0)
    );
  }

  getToken(): string | null {
    return localStorage.getItem(this.accessTokenKey);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  isPosSession(): boolean {
    return this.getClaim('ofs:token_source') === 'login_api';
  }

  getLoggedInUserName(): string {
    const fullName = localStorage.getItem(this.fullNameKey);
    const username = localStorage.getItem(this.usernameKey);

    if (fullName && fullName !== 'undefined' && fullName.trim()) {
      return fullName;
    }

    if (username && username !== 'undefined' && username.trim()) {
      return username;
    }

    return 'Benutzer';
  }

  getRoles(): string[] {
    return this.readStringArrayFromStorage(this.rolesKey);
  }

  isAdmin(): boolean {
    const storedAdminRole = localStorage.getItem(this.isAdminRoleKey);

    if (storedAdminRole !== null) {
      return storedAdminRole === 'true';
    }

    return this.getClaimAsBoolean('ofs:i_a');
  }

  isSuperAdmin(): boolean {
    return (
      this.getClaimAsBoolean('ofs:i_sa') ||
      this.getClaimAsBoolean('ofs:i_s_a')
    );
  }

  isCopagoAdmin(): boolean {
    return (
      this.getClaimAsBoolean('ofs:i_ca') ||
      this.getClaimAsBoolean('ofs:i_c_a')
    );
  }

  isNormalUser(): boolean {
    return this.getClaimAsBoolean('ofs:i_nu');
  }

  isAnyAdmin(): boolean {
    return this.isAdmin() || this.isSuperAdmin() || this.isCopagoAdmin();
  }

  mustChangePassword(): boolean {
    return (
      this.getClaimAsBoolean('ofs:mcp') ||
      this.getClaimAsBoolean('ofs:m_c_p')
    );
  }

  canSeeBenutzerverwaltung(): boolean {
    return this.isAdmin() || this.isSuperAdmin() || this.isCopagoAdmin();
  }

  canAccessModule(moduleName: string): boolean {
    this.permissionVersion();

    if (this.isPosSession()) {
      return this.getAllowedModules().some(
        (module) => module.toLowerCase() === moduleName.toLowerCase()
      );
    }

    if (this.isCopagoAdmin()) {
      return false;
    }

    if (this.isSuperAdmin() || this.isAdmin() || this.hasModulesUnrestricted()) {
      return true;
    }

    return this.getAllowedModules().some(
      (module) => module.toLowerCase() === moduleName.toLowerCase()
    );
  }

  hasModulesUnrestricted(): boolean {
    return (
      this.getClaimAsBoolean('ofs:mu') ||
      this.getClaimAsBoolean('ofs:m_u')
    );
  }

  hasFilialenUnrestricted(): boolean {
    return (
      this.isCopagoAdmin() ||
      this.isSuperAdmin() ||
      this.isAdmin() ||
      this.getClaimAsBoolean('ofs:fu') ||
      this.getClaimAsBoolean('ofs:f_u')
    );
  }

  canAccessFiliale(filialeId: string | number): boolean {
    this.permissionVersion();

    if (this.hasFilialenUnrestricted()) {
      return true;
    }

    const normalizedFilialeId = String(filialeId);

    return this.getAllowedFilialen().some(
      (allowedFilialeId) => allowedFilialeId === normalizedFilialeId
    );
  }

  getAllowedModules(): string[] {
    this.permissionVersion();

    return this.readStringArrayFromStorage(this.modulesKey);
  }

  getAllowedFilialen(): string[] {
    this.permissionVersion();

    return this.readStringArrayFromStorage(this.filialenKey);
  }

  getAllowedFilialenDetails(): FilialeLookupDto[] {
    this.permissionVersion();

    const value = localStorage.getItem(this.filialenDetailsKey);

    if (!value) {
      return [];
    }

    try {
      const parsed = JSON.parse(value);

      return Array.isArray(parsed) ? parsed as FilialeLookupDto[] : [];
    } catch {
      return [];
    }
  }

  hasValidSession(): boolean {
    const hasValidToken =
      this.hasValidAccessToken() ||
      this.hasValidRefreshToken();

    return (
      hasValidToken &&
      this.isSessionInitializationComplete()
    );
  }

  restoreSession(): void {
    this.isLoggedIn.set(this.hasValidSession());
  }

  updateTokens(response: LoginResponse): void {
    this.storeTokens(response);
    this.storeUserDataFromToken(response.accessToken);

    this.isLoggedIn.set(
      this.hasValidSession()
    );
  }

  clearSession(): void {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.accessTokenExpiryKey);
    localStorage.removeItem(this.refreshTokenExpiryKey);
    localStorage.removeItem(this.sessionReadyKey);
    localStorage.removeItem(this.usernameKey);
    localStorage.removeItem(this.fullNameKey);

    this.clearRuntimePermissions();

    this.isLoggedIn.set(false);
  }

  shouldRefreshAccessToken(bufferSeconds = 30): boolean {
    const accessToken = this.getToken();
    const refreshToken = this.getRefreshToken();
    const accessTokenExpiry = localStorage.getItem(this.accessTokenExpiryKey);

    if (!accessToken || !refreshToken || !accessTokenExpiry) {
      return false;
    }

    const expiryTime = new Date(accessTokenExpiry).getTime();
    const refreshBeforeTime = Date.now() + bufferSeconds * 1000;

    return expiryTime <= refreshBeforeTime;
  }

  getFirstAllowedRoute(): string {
    this.permissionVersion();

    if (this.isPosSession() && this.getAllowedModules().length === 0) {
      return '/keine-module';
    }

    if (this.isCopagoAdmin()) {
      return '/benutzerverwaltung';
    }

    if (this.canAccessModule('Bestellungen')) {
      return '/bestellungen';
    }

    if (this.canAccessModule('Wareneingaenge')) {
      return '/wareneingaenge';
    }

    if (this.canAccessModule('Retouren')) {
      return '/retouren';
    }

    if (this.canAccessModule('Entlastungen')) {
      return '/entlastungen';
    }

    if (this.canAccessModule('Inventuren')) {
      return '/inventur';
    }

    if (this.canSeeBenutzerverwaltung()) {
      return '/benutzerverwaltung';
    }

    return '/keine-module';
  }

  getCurrentUserId(): string | null {
    const userId =
      this.getClaim('sub') ??
      this.getClaim('ofs:uid') ??
      this.getClaim('ofs:u_id');

    return typeof userId === 'string' ? userId : null;
  }

  private clearRuntimePermissions(): void {
    localStorage.removeItem(this.rolesKey);
    localStorage.removeItem(this.modulesKey);
    localStorage.removeItem(this.filialenKey);
    localStorage.removeItem(this.filialenDetailsKey);
    localStorage.removeItem(this.isAdminRoleKey);

    this.permissionVersion.update((value) => value + 1);
  }

  private hasValidAccessToken(): boolean {
    const accessToken = this.getToken();
    const accessTokenExpiry = localStorage.getItem(this.accessTokenExpiryKey);

    if (!accessToken || !accessTokenExpiry) {
      return false;
    }

    return new Date(accessTokenExpiry).getTime() > Date.now();
  }

  private hasValidRefreshToken(): boolean {
    const refreshToken = this.getRefreshToken();
    const refreshTokenExpiry = localStorage.getItem(this.refreshTokenExpiryKey);

    if (!refreshToken || !refreshTokenExpiry) {
      return false;
    }

    return new Date(refreshTokenExpiry).getTime() > Date.now();
  }

  private storeTokens(response: LoginResponse): void {
    localStorage.setItem(this.accessTokenKey, response.accessToken);
    localStorage.setItem(this.refreshTokenKey, response.refreshToken);
    localStorage.setItem(this.accessTokenExpiryKey, response.accessTokenExpiry);
    localStorage.setItem(this.refreshTokenExpiryKey, response.refreshTokenExpiry);
  }

  private storeUserDataFromToken(accessToken: string): void {
    const claims = this.decodeAccessToken(accessToken);

    const fullName = claims['ofs:fn'] ?? claims['ofs:f_n'];

    const username =
      claims['unique_name'] ??
      claims['name'] ??
      claims['sub'] ??
      claims['ofs:uid'] ??
      claims['ofs:u_id'];

    if (typeof fullName === 'string' && fullName.trim()) {
      localStorage.setItem(this.fullNameKey, fullName);
    } else {
      localStorage.removeItem(this.fullNameKey);
    }

    if (typeof username === 'string' && username.trim()) {
      localStorage.setItem(this.usernameKey, username);
    } else {
      localStorage.removeItem(this.usernameKey);
    }
  }

  private getClaim(claimName: string): unknown {
    const token = this.getToken();

    if (!token) {
      return null;
    }

    const claims = this.decodeAccessToken(token);

    return claims[claimName] ?? null;
  }

  private getClaimAsBoolean(claimName: string): boolean {
    const value = this.getClaim(claimName);

    return value === true || value === 'true';
  }

  private decodeAccessToken(token: string): Record<string, unknown> {
    const payload = token.split('.')[1];

    if (!payload) {
      return {};
    }

    try {
      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(
        Math.ceil(normalizedPayload.length / 4) * 4,
        '='
      );

      const decodedPayload = decodeURIComponent(
        atob(paddedPayload)
          .split('')
          .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join('')
      );

      return JSON.parse(decodedPayload) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private getTokenExpiryIso(claims: Record<string, unknown>): string | null {
    const exp = claims['exp'];

    if (typeof exp !== 'number' || !Number.isFinite(exp)) {
      return null;
    }

    const expiryMs = exp * 1000;

    if (expiryMs <= Date.now()) {
      return null;
    }

    return new Date(expiryMs).toISOString();
  }

  private readStringArrayFromStorage(key: string): string[] {
    const value = localStorage.getItem(key);

    if (!value) {
      return [];
    }

    try {
      const parsed = JSON.parse(value);

      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  private completeSessionInitialization(): void {
    localStorage.setItem(
      this.sessionReadyKey,
      'true'
    );

    this.isLoggedIn.set(true);
  }

  private isSessionInitializationComplete(): boolean {
    return (
      localStorage.getItem(
        this.sessionReadyKey
      ) === 'true'
    );
  }
}